import { useState, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  Download,
  Eye,
  Trash2,
  FileText,
  Image,
  File,
  Paperclip,
  X
} from 'lucide-react';
import toast from 'react-hot-toast';

import { apiHelper } from '@services/api';
import { useAuth } from '@context/AuthContext';
import Button, { IconButton } from '@components/common/Button';
import Modal, { ModalBody } from '@components/common/Modal';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const TicketAttachments = ({ ticketId, attachments = [] }) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const fileInputRef = useRef(null);

  const { user, hasPermission } = useAuth();
  const queryClient = useQueryClient();

  // Upload mutation
  const uploadMutation = useMutation(
    async (files) => {
      const formData = new FormData();
      files.forEach((file) => formData.append('attachments', file));
      const resp = await apiHelper.post(
        `/tickets/${ticketId}/attachments`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return resp.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ticket', ticketId]);
        setSelectedFiles([]);
        toast.success('Files uploaded successfully');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to upload files');
      }
    }
  );

  // Delete mutation
  const deleteMutation = useMutation(
    async (attachmentId) => {
      await apiHelper.delete(`/tickets/${ticketId}/attachments/${attachmentId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['ticket', ticketId]);
        toast.success('Attachment deleted');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Failed to delete attachment');
      }
    }
  );

  const canUpload = hasPermission?.('can_create') || hasPermission?.('can_edit');
  const canDelete = hasPermission?.('can_delete') || user?.role === 'admin';

  const handleFileSelect = (filesList) => {
    const incoming = Array.from(filesList || []);
    const valid = incoming.filter((file) => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast.error(`${file.name} is too large. Max ${MAX_FILE_SIZE_BYTES / (1024 * 1024)}MB`);
        return false;
      }
      return true;
    });
    setSelectedFiles((s) => [...s, ...valid]);
  };

  const removeSelectedFile = (index) => {
    setSelectedFiles((s) => s.filter((_, i) => i !== index));
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) {
      toast.error('Please select files to upload');
      return;
    }
    uploadMutation.mutate(selectedFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const openFileDialog = () => fileInputRef.current?.click();

  const getFileIcon = (fileType) => {
    if (!fileType) return File;
    if (fileType.startsWith('image/')) return Image;
    if (fileType.includes('pdf')) return FileText;
    return File;
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round((bytes / Math.pow(1024, i)) * 100) / 100} ${sizes[i]}`;
  };

  const handlePreview = (attachment) => {
    setPreviewFile(attachment);
    setShowPreview(true);
  };

  const handleDownload = async (attachment) => {
    try {
      const resp = await apiHelper.get(
        `/tickets/${ticketId}/attachments/${attachment.id}/download`,
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.original_filename || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download file');
    }
  };

  return (
    <div className="space-y-8">
      {/* Upload area */}
      {canUpload && (
        <motion.div 
          className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden"
          initial={{ opacity: 0, y: 8 }} 
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h4 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <Upload className="w-5 h-5 text-gray-600" />
              Upload Files
            </h4>
          </div>

          <div className="p-6">
            <div
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && openFileDialog()}
              onClick={openFileDialog}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                isDragging 
                  ? 'border-blue-400 bg-blue-50 scale-[1.02] shadow-lg' 
                  : 'border-gray-300 hover:border-blue-300 hover:bg-blue-50/50'
              }`}
              aria-label="Drop files here or click to upload"
            >
              <div className={`transition-transform duration-200 ${isDragging ? 'scale-110' : ''}`}>
                <Upload className={`w-12 h-12 mx-auto mb-4 ${
                  isDragging ? 'text-blue-500' : 'text-gray-400'
                }`} />
                <h5 className="text-lg font-semibold text-gray-900 mb-2">
                  {isDragging ? 'Drop your files here!' : 'Upload supporting files'}
                </h5>
                <p className="text-sm text-gray-600 mb-6 max-w-sm mx-auto">
                  Drag & drop files here, or click to browse. 
                  <br />
                  <span className="text-xs text-gray-500">
                    Max 10MB per file • Images, PDFs, Documents, Spreadsheets
                  </span>
                </p>

                <Button 
                  type="button" 
                  variant="outline" 
                  size="md" 
                  leftIcon={<Paperclip className="w-4 h-4" />}
                  className="px-6"
                >
                  Choose Files
                </Button>
              </div>

              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
                onChange={(e) => handleFileSelect(e.target.files)}
                className="hidden"
                aria-hidden
              />
            </div>
          </div>

          {/* Selected files */}
          {selectedFiles.length > 0 && (
            <div className="border-t border-gray-200 pt-6">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Selected Files ({selectedFiles.length})
                </h4>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setSelectedFiles([])} 
                  disabled={uploadMutation.isLoading}
                  className="text-gray-500"
                >
                  Clear All
                </Button>
              </div>

              <div className="space-y-3 mb-4">
                {selectedFiles.map((file, idx) => (
                  <motion.div
                    key={`${file.name}-${idx}`}
                    className="flex items-center justify-between p-4 bg-blue-50 border border-blue-200 rounded-lg"
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                  >
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <FileText className="w-5 h-5 text-blue-600 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-semibold text-gray-900 truncate">{file.name}</p>
                        <p className="text-xs text-gray-600 flex items-center gap-1">
                          <span>{formatFileSize(file.size)}</span>
                          <span>•</span>
                          <span>{file.type || 'Unknown type'}</span>
                        </p>
                      </div>
                    </div>

                    <IconButton
                      size="sm"
                      variant="ghost"
                      onClick={() => removeSelectedFile(idx)}
                      className="text-gray-400 hover:text-red-600 hover:bg-red-50 flex-shrink-0"
                      aria-label={`Remove ${file.name}`}
                    >
                      <X className="w-4 h-4" />
                    </IconButton>
                  </motion.div>
                ))}
              </div>

              <Button
                onClick={handleUpload}
                loading={uploadMutation.isLoading}
                disabled={uploadMutation.isLoading}
                leftIcon={<Upload className="w-4 h-4" />}
                size="md"
                className="w-full"
              >
                {uploadMutation.isLoading 
                  ? 'Uploading...' 
                  : `Upload ${selectedFiles.length} file${selectedFiles.length !== 1 ? 's' : ''}`}
              </Button>
            </div>
          )}
        </motion.div>
      )}

      {/* Existing attachments */}
      <div>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-3">
            <Paperclip className="w-6 h-6 text-blue-600" />
            Attachments
            <span className="ml-2 px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium">
              {attachments.length}
            </span>
          </h3>
        </div>

        {attachments.length > 0 ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <AnimatePresence>
              {attachments.map((att, i) => {
                const Icon = getFileIcon(att.file_type);
                const isImage = att.file_type?.startsWith?.('image/');
                return (
                  <motion.div
                    key={att.id}
                    className="bg-white border border-gray-200 rounded-xl p-6 hover:shadow-md hover:border-gray-300 transition-all group"
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.18, delay: i * 0.03 }}
                  >
                    {/* File Header */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-blue-50 transition-colors">
                        <Icon className={`w-6 h-6 ${isImage ? 'text-green-600' : 'text-blue-600'}`} />
                      </div>

                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-semibold text-gray-900 truncate mb-1" title={att.original_filename}>
                          {att.original_filename}
                        </h4>
                        <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                          <span className="inline-flex items-center gap-1 bg-gray-100 px-2 py-1 rounded-md font-medium">
                            {formatFileSize(att.file_size)}
                          </span>
                          <span>{new Date(att.uploaded_at).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}</span>
                        </div>
                        {att.uploaded_by && (
                          <p className="text-xs text-gray-500 mt-1">
                            Uploaded by {att.uploaded_by.first_name} {att.uploaded_by.last_name}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1">
                        {isImage && (
                          <IconButton 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handlePreview(att)} 
                            tooltip="Preview image"
                            className="text-gray-500 hover:text-blue-600 hover:bg-blue-50"
                          >
                            <Eye className="w-4 h-4" />
                          </IconButton>
                        )}

                        <IconButton 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => handleDownload(att)} 
                          tooltip="Download file"
                          className="text-gray-500 hover:text-green-600 hover:bg-green-50"
                        >
                          <Download className="w-4 h-4" />
                        </IconButton>
                      </div>

                      {canDelete && (
                        <IconButton
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            if (confirm('Are you sure you want to delete this attachment?')) {
                              deleteMutation.mutate(att.id);
                            }
                          }}
                          className="text-gray-400 hover:text-red-600 hover:bg-red-50"
                          tooltip="Delete attachment"
                        >
                          <Trash2 className="w-4 h-4" />
                        </IconButton>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        ) : (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            className="text-center py-12 bg-gray-50 rounded-xl border-2 border-dashed border-gray-300"
          >
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Paperclip className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No attachments yet</h3>
            <p className="text-gray-600 max-w-md mx-auto">
              {canUpload 
                ? 'Upload files to provide additional context, screenshots, or documentation for this ticket.' 
                : 'No files have been attached to this ticket.'}
            </p>
          </motion.div>
        )}
      </div>

      {/* Preview Modal */}
      <Modal isOpen={showPreview} onClose={() => setShowPreview(false)} size="4xl" title="">
        <ModalBody className="p-0">
          {previewFile ? (
            <div>
              {/* Modal Header */}
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{previewFile.original_filename}</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      {formatFileSize(previewFile.file_size)} • 
                      {new Date(previewFile.uploaded_at).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      leftIcon={<Download className="w-4 h-4" />} 
                      onClick={() => handleDownload(previewFile)}
                    >
                      Download
                    </Button>
                  </div>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {previewFile.file_type?.startsWith('image/') ? (
                  <div className="text-center">
                    <img
                      src={`/api/tickets/${ticketId}/attachments/${previewFile.id}/preview`}
                      alt={previewFile.original_filename}
                      className="max-w-full max-h-[70vh] mx-auto rounded-lg shadow-lg border border-gray-200"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextElementSibling.style.display = 'block';
                      }}
                    />
                    <div className="hidden bg-gray-100 rounded-lg p-8 text-center">
                      <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-600">Image preview is not available.</p>
                    </div>
                  </div>
                ) : (
                  <div className="bg-gray-50 rounded-lg p-12 text-center">
                    <FileText className="w-20 h-20 text-gray-400 mx-auto mb-4" />
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Preview not available</h4>
                    <p className="text-gray-600 mb-6">This file type cannot be previewed in the browser.</p>
                    <Button 
                      variant="primary" 
                      leftIcon={<Download className="w-4 h-4" />} 
                      onClick={() => handleDownload(previewFile)}
                    >
                      Download to view
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default TicketAttachments;
